import { Controller, useForm, useWatch } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { cn } from '@/core/lib/utils';
import { FIELD_TYPE } from '../types';
import type { FieldType, ProjectField } from '../types';
import { createFieldSchema } from '../schemas';
import type { CreateFieldInput, UpdateFieldInput } from '../schemas';
import { getFieldRegistryEntry } from '../registry/field-registry';
import { useCreateProjectField, useUpdateProjectField } from '../hooks/useProjectFieldsMutations';
import { FieldOptionEditor } from './FieldOptionEditor';
import { DefaultValueField } from './DefaultValueField';
import { FieldDisplaySettings } from './FieldDisplaySettings';

interface FieldCreateFormProps {
  fieldType: FieldType;
  projectId: string;
  editingField?: ProjectField;
  onSuccess: () => void;
  onCancel: () => void;
}

const hasOptions = (type: FieldType) =>
  type === FIELD_TYPE.Select || type === FIELD_TYPE.MultiSelect;

export function FieldCreateForm({
  fieldType,
  projectId,
  editingField,
  onSuccess,
  onCancel,
}: FieldCreateFormProps) {
  const { t } = useTranslation('customFields');
  const isEditing = !!editingField;
  const isCurrency = fieldType === FIELD_TYPE.Currency;
  const entry = getFieldRegistryEntry(fieldType);

  const createMutation = useCreateProjectField(projectId);
  const updateMutation = useUpdateProjectField(projectId);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<CreateFieldInput>({
    resolver: zodResolver(createFieldSchema) as Resolver<CreateFieldInput>,
    defaultValues: {
      name: editingField?.name ?? '',
      type: fieldType,
      description: editingField?.description ?? '',
      defaultValue: editingField?.defaultValue ?? '',
      required: editingField?.required ?? false,
      pinned: false,
      visibleToGuests: false,
      order: editingField?.order ?? 0,
      typeConfig: editingField?.typeConfig
        ? (JSON.parse(editingField.typeConfig) as { permissions?: string })?.permissions
        : undefined,
      currencyCode: editingField?.typeConfig
        ? (JSON.parse(editingField.typeConfig) as { currency?: string })?.currency
        : undefined,
      options:
        editingField?.options?.map((o) => ({
          id: o.id ?? undefined,
          name: o.name ?? '',
          value: o.value ?? '',
          color: o.color ?? undefined,
          orderIndex: o.orderIndex,
        })) ?? (hasOptions(fieldType) ? [] : undefined),
    },
  });

  // `typeConfig` is one opaque JSON blob on the backend shared by unrelated concerns —
  // permissions (all field types) and currency code (Currency only) — so both must be
  // merged into the same object instead of one overwriting the other.
  const serializeTypeConfig = (
    permissions: string | undefined,
    currencyCode: string | undefined,
  ) => {
    const merged: { permissions?: string; currency?: string } = {};
    if (permissions) merged.permissions = permissions;
    if (isCurrency && currencyCode) merged.currency = currencyCode;
    return Object.keys(merged).length > 0 ? JSON.stringify(merged) : undefined;
  };

  const handleSubmit = (data: CreateFieldInput) => {
    if (isEditing && editingField?.id) {
      const updatePayload: UpdateFieldInput = {
        name: data.name,
        description: data.description,
        defaultValue: data.defaultValue,
        required: data.required,
        order: data.order,
        typeConfig: serializeTypeConfig(data.typeConfig, data.currencyCode),
        options: data.options,
      };
      updateMutation.mutate({ fieldId: editingField.id, payload: updatePayload }, { onSuccess });
    } else {
      createMutation.mutate(
        { ...data, typeConfig: serializeTypeConfig(data.typeConfig, data.currencyCode) },
        { onSuccess },
      );
    }
  };

  const currentOptions = useWatch({ control: form.control, name: 'options' }) ?? [];

  const titleKey = isEditing ? 'createForm.titleEdit' : 'createForm.titleCreate';
  const typeLabel = t(entry.labelKey.replace('customFields.', ''));

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="custom-fields-create-form">
      <h3 className="text-base font-semibold">{t(titleKey, { type: typeLabel })}</h3>

      <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4">
        {/* Name */}
        <FormFieldInput
          control={form.control}
          name="name"
          label={t('createForm.name')}
          floatingLabel
          data-testid="custom-fields-name-input"
        />

        {/* Options editor for Select / MultiSelect */}
        {hasOptions(fieldType) && (
          <Controller
            control={form.control}
            name="options"
            render={({ field }) => (
              <FieldOptionEditor
                value={field.value ?? []}
                onChange={field.onChange}
                disabled={isPending}
                projectId={isEditing ? projectId : undefined}
                fieldId={isEditing ? (editingField?.id ?? undefined) : undefined}
              />
            )}
          />
        )}

        {/* More settings */}
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {t('createForm.moreSettings')}
        </p>

        <FormFieldInput
          control={form.control}
          name="description"
          label={t('createForm.description')}
          floatingLabel
          data-testid="custom-fields-description-input"
        />

        <DefaultValueField
          fieldType={fieldType}
          control={form.control}
          options={currentOptions}
          disabled={isPending}
        />

        <FieldDisplaySettings control={form.control} isCurrency={isCurrency} />

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isPending}
            data-testid="custom-fields-cancel-button"
          >
            {t('createForm.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={isPending}
            aria-busy={isPending}
            data-testid="custom-fields-submit-button"
            className={cn(isPending && 'opacity-70')}
          >
            {isEditing ? t('createForm.save') : t('createForm.create')}
          </Button>
        </div>
      </form>
    </div>
  );
}
